import { useFocusEffect, useNavigation } from "@react-navigation/native";
import React from "react";
import firebase from "firebase/app";
import { ActivityIndicator, ScrollView, StyleSheet, View } from "react-native";
import { useSelector } from "react-redux";
import { FormProvider, useForm, useWatch } from "react-hook-form";

import { tRep, tRepCategory, tVendorData } from "tmt_api/src/types/types.validator";

import { kColors } from "src/kColors";
import { tImagePickerValue } from "src/components/forms/picker";
import { repCategories, usStateOptions } from "src/util/comboBoxData";
import { useIsMobile } from "src/hooks/ui";
import { PdStack, PdText } from "src/components/layout";
import { kStyleConst } from "src/kStyle";
import { PdFormLabel } from "src/components/form-control";
import {
  PdHookFormCheckboxGroup,
  PdHookFormErrorMessage,
  PdHookFormImagePicker,
  PdHookFormInput,
  PdHookFormInputPdf,
  PdHookFormInputPhone,
  PdHookFormSelect,
} from "src/components/hook-form";
import { PdDivider } from "src/components/layout/table";
import { PdButton, PdButtonLabel } from "src/components/forms/button";
import { getSafeUrl } from "src/util/functions";
import { useAppDispatch } from "src/redux";
import { updateRepProfile } from "src/redux/company/actions";
import { useConfirmModal, useVendorSetupAccount } from "src/hooks/modals";
import { logout, sendResetPasswordEmail } from "src/redux/user/actions";
import { setProfilePreview } from "src/redux/profile/actions";
import { kRoute } from "src/kRoute";
import { documentRep } from "src/db/rep.db";
import { PdFormCard, PdSectionBadge, PdSectionBadgeLabel } from "./components";
import { tPdInputPdfValue } from "src/components/forms/input";

type tRepFormData = {
  name: string;
  website: string;
  linkedin: string;
  instagram: string;
  facebook: string;
  twitter: string;
  aboutMe: string;
  businessName: string;
  phone: string;
  email: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: { value: string; label: string } | null;
  zipCode: string;
  categories: tRepCategory[];
  taxInfo: tPdInputPdfValue;
  taxId: string;
  avatar: tImagePickerValue | null;
};

const RepForm: React.FC<{ data: tRep }> = ({ data }) => {
  const isMobile = useIsMobile();
  const navigation = useNavigation();
  const dispatch = useAppDispatch();
  const rUser = useSelector((state) => state.UserReducer.profile);
  const confirmModal = useConfirmModal();
  const vendorSetupAccount = useVendorSetupAccount();
  const formMethods = useForm<tRepFormData>({
    defaultValues: {
      name: data.name || "",
      website: data.website || "",
      linkedin: data.socialMedia?.linkedin ?? "",
      instagram: data.socialMedia?.instagram ?? "",
      facebook: data.socialMedia?.facebook ?? "",
      twitter: data.socialMedia?.twitter ?? "",
      aboutMe: data.aboutMe || "",
      businessName: data.company || "",
      phone: data.phone || "",
      email: data.email || "",
      addressLine1: data.address.address1,
      addressLine2: data.address.address2,
      city: data.address.city,
      state: data.address.state_or_province
        ? usStateOptions.find((state) => [state.label, state.value].includes(data.address.state_or_province)) ?? null
        : null,
      zipCode: data.address.postal_code || "",
      categories: data.category || [],
      taxInfo: data.taxInfo ? { uri: data.taxInfo.url, name: data.taxInfo.name } : null,
      taxId: data.taxId || "",
      avatar: data.avatar ? { uri: data.avatar } : null,
    },
  });
  const categories = useWatch({ name: "categories", control: formMethods.control });
  const [isPreviewing, setPreviewing] = React.useState(false);
  const [isBankConnected, setBankConnected] = React.useState(!!data.vendorId);
  const [isSubmitting, setSubmitting] = React.useState(false);

  const handleOnPressPreview = async () => {
    setPreviewing(true);
    const formData = formMethods.getValues();
    const rep: tRep = {
      ...data,
      name: formData.name,
      website: formData.website ?? "",
      socialMedia: {
        linkedin: getSafeUrl(formData.linkedin),
        instagram: getSafeUrl(formData.instagram),
        facebook: getSafeUrl(formData.facebook),
        twitter: getSafeUrl(formData.twitter),
      },
      aboutMe: formData.aboutMe,
      company: formData.businessName,
      phone: formData.phone,
      email: formData.email,
      address: {
        ...data.address,
        address1: formData.addressLine1 ?? "",
        address2: formData.addressLine2,
        city: formData.city,
        state_or_province: formData.state?.value ?? "",
        postal_code: formData.zipCode ?? "",
      },
      avatar: formData.avatar?.uri ?? "",
      category: formData.categories,
    };
    dispatch(setProfilePreview({ type: "rep", rep }));
    navigation.navigate(kRoute.P09_01);
    setPreviewing(false);
  };

  const handleOnPressSetupAccount = async () => {
    const defaultValue: tVendorData = {
      businessName: data.name ?? "",
      businessDomain: "",
      ein: data.taxId ?? "",
      url: "",
      emailAddress: data.email ?? "",
      address1: data.address.address1,
      address2: data.address.address2 ?? "",
      city: data.address.city,
      state: data.address.state_or_province,
      zipCode: data.address.postal_code,
      phone: data.phone ?? "",
      country: data.address.country_code,
      accountNumber: "",
      routingNumber: "",
    };
    const response = await vendorSetupAccount.openAsync({ mode: "rep", defaultValue, isEditing: isBankConnected });
    if (response.status === "closed") return;
    setBankConnected(true);
  };

  const handleOnPressChangePassword = async () => {
    const response = await confirmModal.openAsync({
      title: "Reset Password",
      description:
        "For your security, in order to reset your password, you will be logged out. Would you like to continue?",
    });
    if (response.status === "closed") return;

    dispatch(sendResetPasswordEmail(data.email));
    dispatch(logout());
  };

  const handleOnSubmit = formMethods.handleSubmit(async (formData) => {
    try {
      setSubmitting(true);
      const rep: tRep = {
        ...data,
        name: formData.name,
        website: formData.website ?? "",
        socialMedia: {
          linkedin: getSafeUrl(formData.linkedin),
          instagram: getSafeUrl(formData.instagram),
          facebook: getSafeUrl(formData.facebook),
          twitter: getSafeUrl(formData.twitter),
        },
        aboutMe: formData.aboutMe,
        company: formData.businessName,
        phone: formData.phone,
        email: formData.email,
        address: {
          ...data.address,
          address1: formData.addressLine1 ?? "",
          address2: formData.addressLine2,
          city: formData.city,
          state_or_province: formData.state?.value ?? "",
          postal_code: formData.zipCode ?? "",
        },
        avatar: formData.avatar?.uri ?? "",
        category: formData.categories,
      };

      if (formData.taxInfo?.file) {
        const snapshot = await firebase.storage().ref(`rep/${data.id}/uploads/tax_info.pdf`).put(formData.taxInfo.file);
        const url: string = await snapshot.ref.getDownloadURL();
        rep.taxInfo = { name: formData.taxInfo?.name ?? "", url };
      }
      if (formData.avatar?.file) {
        const fileExtension = formData.avatar.file.type.replace(/^.*\//gm, "");
        const snapshot = await firebase
          .storage()
          .ref(`rep/${data.id}/uploads/profile.${fileExtension}`)
          .put(formData.avatar.file);
        rep.avatar = await snapshot.ref.getDownloadURL();
      }
      await dispatch(updateRepProfile(rep, rUser));
      setSubmitting(false);
    } catch (error) {
      setSubmitting(false);
    }
  });

  return (
    <FormProvider {...formMethods}>
      <PdButton
        isLoading={isPreviewing}
        style={isMobile ? styles.mobilePreviewButton : styles.webPreviewButton}
        onPress={handleOnPressPreview}
      >
        <PdButtonLabel>Preview</PdButtonLabel>
      </PdButton>
      <PdStack direction={isMobile ? "vertical" : "horizontal"} layout={[1, 1]} gap={3} style={styles.formSection}>
        <PdFormCard style={styles.zIndex1}>
          <View style={styles.formSectionTitle}>
            <PdSectionBadge>
              <PdSectionBadgeLabel>1</PdSectionBadgeLabel>
            </PdSectionBadge>
            <PdText variant="fieldset" style={styles.ml2}>
              The Basics
            </PdText>
          </View>
          <PdDivider style={styles.formSectionDivider} />
          <View style={styles.mb3}>
            <PdFormLabel>Primary Contact Full Name *</PdFormLabel>
            <PdHookFormInput name="name" rules={{ required: "Required" }} onSubmitEditing={handleOnSubmit} />
            <PdHookFormErrorMessage name="name" />
          </View>
          <View style={styles.mb3}>
            <PdFormLabel>Business / Company Name *</PdFormLabel>
            <PdHookFormInput name="businessName" rules={{ required: "Required" }} onSubmitEditing={handleOnSubmit} />
            <PdHookFormErrorMessage name="businessName" />
          </View>
          <PdStack direction={isMobile ? "vertical" : "horizontal"} layout={[1, 1]} gap={3} style={styles.formSection}>
            <View>
              <PdFormLabel>Primary Phone Number *</PdFormLabel>
              <PdHookFormInputPhone
                name="phone"
                placeholder="(XXX) XXX-XXXX"
                autoCompleteType="tel"
                rules={{ required: "Required" }}
                onSubmitEditing={handleOnSubmit}
              />
              <PdHookFormErrorMessage name="phone" />
            </View>
            <View>
              <PdFormLabel>Primary Email *</PdFormLabel>
              <PdHookFormInput
                isDisabled
                name="email"
                rules={{ required: "Required" }}
                onSubmitEditing={handleOnSubmit}
              />
              <PdHookFormErrorMessage name="email" />
            </View>
          </PdStack>
          <View style={styles.mb3}>
            <PdFormLabel>Primary Adress Line 1 *</PdFormLabel>
            <PdHookFormInput name="addressLine1" rules={{ required: "Required" }} onSubmitEditing={handleOnSubmit} />
            <PdHookFormErrorMessage name="addressLine1" />
          </View>
          <View style={styles.mb3}>
            <PdFormLabel>Primary Adress Line 2</PdFormLabel>
            <PdHookFormInput name="addressLine2" onSubmitEditing={handleOnSubmit} />
          </View>
          <PdStack
            direction={isMobile ? "vertical" : "horizontal"}
            layout={[2, 1, 1]}
            gap={3}
            style={styles.selectFormSection}
          >
            <View>
              <PdFormLabel>Primary City *</PdFormLabel>
              <PdHookFormInput name="city" rules={{ required: "Required" }} onSubmitEditing={handleOnSubmit} />
              <PdHookFormErrorMessage name="city" />
            </View>
            <View style={styles.zIndex1}>
              <PdFormLabel>State *</PdFormLabel>
              <PdHookFormSelect
                name="state"
                rules={{ required: "Required" }}
                options={usStateOptions}
                isClearable={false}
                onSubmitEditing={handleOnSubmit}
              />
              <PdHookFormErrorMessage name="state" />
            </View>
            <View>
              <PdFormLabel>Zip *</PdFormLabel>
              <PdHookFormInput name="zipCode" rules={{ required: "Required" }} onSubmitEditing={handleOnSubmit} />
              <PdHookFormErrorMessage name="zipCode" />
            </View>
          </PdStack>
          <View>
            <PdFormLabel>What category best describes your Brands (select up to 3) *</PdFormLabel>
            <PdHookFormCheckboxGroup
              name="categories"
              isDisabled={categories.length > 2}
              rules={{
                required: "Required",
                validate: (value: string[]) => (value.length > 3 ? "You can select up to 3 categories" : true),
              }}
              style={styles.mt2}
              options={repCategories}
            />
            <PdHookFormErrorMessage name="categories" />
          </View>
        </PdFormCard>
        <PdFormCard>
          <View style={styles.formSectionTitle}>
            <PdSectionBadge>
              <PdSectionBadgeLabel>2</PdSectionBadgeLabel>
            </PdSectionBadge>
            <PdText variant="fieldset" style={styles.ml2}>
              My Profile
            </PdText>
          </View>
          <PdDivider style={styles.formSectionDivider} />
          <View style={styles.mb3}>
            <PdFormLabel>Profile Image</PdFormLabel>
            <PdStack style={styles.profileImageFormLine} direction={isMobile ? "vertical" : "horizontal"}>
              <PdHookFormImagePicker
                name="avatar"
                style={styles.avatarImagePicker}
                mobileStyle={styles.mobileAvatarImagePicker}
                webStyle={webStyles.avatarImagePicker}
              />
              <PdText style={styles.profileImageText}>Upload an image of your self of company logo.</PdText>
            </PdStack>
          </View>
          <View style={styles.mb3}>
            <PdFormLabel>About Me</PdFormLabel>
            <PdHookFormInput
              name="aboutMe"
              multiline
              numberOfLines={12}
              placeholder="Write a brief bio about you and your business. This will be visible to brands and retailers when they view your profile."
            />
          </View>
          <View style={styles.mb3}>
            <PdFormLabel>Website</PdFormLabel>
            <PdHookFormInput name="website" onSubmitEditing={handleOnSubmit} />
          </View>
          <View style={styles.mb3}>
            <PdFormLabel>Linkedin</PdFormLabel>
            <PdHookFormInput name="linkedin" onSubmitEditing={handleOnSubmit} />
          </View>
          <View style={styles.mb3}>
            <PdFormLabel>Instagram</PdFormLabel>
            <PdHookFormInput name="instagram" onSubmitEditing={handleOnSubmit} />
          </View>
          <View>
            <PdFormLabel>Facebook</PdFormLabel>
            <PdHookFormInput name="facebook" onSubmitEditing={handleOnSubmit} />
          </View>
        </PdFormCard>
      </PdStack>

      <PdFormCard>
        <View style={styles.formSectionTitle}>
          <PdSectionBadge>
            <PdSectionBadgeLabel>3</PdSectionBadgeLabel>
          </PdSectionBadge>
          <PdText variant="fieldset" style={styles.ml2}>
            Admin
          </PdText>
        </View>
        <PdDivider style={styles.formSectionDivider} />

        <PdStack direction={isMobile ? "vertical" : "horizontal"} layout={[1, 1]} gap={3}>
          <View>
            <View style={styles.mb3}>
              <PdFormLabel>Resale / Business Certificate</PdFormLabel>
              <PdHookFormInputPdf name="taxInfo" isMobile={isMobile} />
              <PdHookFormErrorMessage name="taxInfo" />
            </View>
            <View style={styles.mb3}>
              <PdFormLabel>Tax ID Number</PdFormLabel>
              <PdHookFormInput name="taxId" />
              <PdHookFormErrorMessage name="taxId" />
            </View>

            <PdButton
              variant="outline"
              onPress={handleOnPressSetupAccount}
              style={isMobile ? styles.mobileBankAccountButton : styles.webBankAccontButton}
            >
              <PdButtonLabel>{isBankConnected ? "Update Bank Account" : "Connect Bank Account"}</PdButtonLabel>
            </PdButton>
            <PdText style={styles.bankAccountText}>
              Commissions you earn will be deposited directly into this bank account.
            </PdText>
          </View>
          <View />
        </PdStack>
      </PdFormCard>

      <PdStack
        direction={isMobile ? "vertical" : "horizontal"}
        isReversed={isMobile}
        style={isMobile ? styles.mobileButtonGroup : styles.webButtonGroup}
      >
        <PdButton variant="outline" onPress={handleOnPressChangePassword}>
          <PdButtonLabel>Change Password</PdButtonLabel>
        </PdButton>
        <PdButton isLoading={isSubmitting} onPress={handleOnSubmit}>
          <PdButtonLabel>Save Changes</PdButtonLabel>
        </PdButton>
      </PdStack>
    </FormProvider>
  );
};

const RepProfile: React.FC = () => {
  const rCompanyId = useSelector((state) => state.UserReducer.profile.companyRefPath.split("/")?.[1]);
  const [rep, setRep] = React.useState<tRep | undefined>(undefined);

  useFocusEffect(
    React.useCallback(() => {
      let unsubscribe: (() => void) | undefined;

      if (rCompanyId) {
        unsubscribe = documentRep(rCompanyId).onSnapshot((document) => {
          setRep(document.data());
        });
      }

      return () => {
        unsubscribe?.();
      };
    }, [rCompanyId]),
  );

  if (!rep) {
    return (
      <View style={styles.spinner}>
        <ActivityIndicator size="large" color={kColors.brandDarkGreen} />
      </View>
    );
  }

  return (
    <ScrollView style={styles.page}>
      <RepForm data={rep} />
    </ScrollView>
  );
};

export default RepProfile;

const styles = StyleSheet.create({
  avatarImagePicker: {
    alignItems: "center",
    backgroundColor: kColors.backgroundGrey,
    borderRadius: 999,
    height: 141,
    justifyContent: "center",
    marginTop: 1 * kStyleConst.kSpacing,
    width: 141,
  },
  bankAccountText: {
    color: kColors.gray500,
    fontSize: 12,
  },
  formSection: {
    marginBottom: kStyleConst.kSpacing * 3,
    width: "100%",
  },
  formSectionDivider: {
    marginBottom: 4 * kStyleConst.kSpacing,
    marginTop: 1 * kStyleConst.kSpacing,
  },
  formSectionTitle: {
    alignItems: "center",
    flexDirection: "row",
  },
  mb3: {
    marginBottom: kStyleConst.kSpacing * 3,
  },
  ml2: {
    marginLeft: kStyleConst.kSpacing * 2,
  },
  mobileAvatarImagePicker: {
    borderRadius: 999,
    height: 141,
    width: 141,
  },
  mobileBankAccountButton: {
    marginBottom: kStyleConst.kSpacing * 1,
  },
  mobileButtonGroup: {
    marginTop: 4 * kStyleConst.kSpacing,
  },
  mobilePreviewButton: {
    marginBottom: kStyleConst.kSpacing * 3,
  },
  mt2: {
    marginTop: kStyleConst.kSpacing * 2,
  },
  page: {
    backgroundColor: kColors.lightGray,
    padding: kStyleConst.kSpacing * 6,
  },
  profileImageFormLine: {
    alignItems: "center",
  },
  profileImageText: {
    color: kColors.gray500,
    fontSize: 12,
  },
  selectFormSection: {
    marginBottom: kStyleConst.kSpacing * 3,
    width: "100%",
    zIndex: 1,
  },
  spinner: {
    alignItems: "center",
    flex: 1,
    justifyContent: "center",
  },
  webBankAccontButton: {
    marginBottom: kStyleConst.kSpacing * 1,
    marginRight: "auto",
  },
  webButtonGroup: {
    justifyContent: "space-between",
    marginTop: 4 * kStyleConst.kSpacing,
  },
  webPreviewButton: {
    marginBottom: kStyleConst.kSpacing * 3,
    marginLeft: "auto",
  },
  zIndex1: {
    zIndex: 1,
  },
});

const webStyles: { [key: string]: React.CSSProperties } = {
  avatarImagePicker: {
    width: 141,
    height: 141,
    borderRadius: 999,
  },
} as const;
